/**
 * useClients — Clients page data hook.
 *
 * Bounded, cursor-paginated fetch backed by:
 *   GET /api/clients/list?limit=50[&cursor=<lastId>]
 *   GET /api/clients/search?q=<query>
 *
 * REPLACES the previous unbounded `onSnapshot(collection(leads))` pattern
 * that streamed the entire tenant history to every open tab.
 *
 * Two modes:
 *   • Browse — cursor pagination, "Load more" appends.
 *   • Search — server-side full-dataset search over the tenant's
 *              `_searchTokens` index. Returns up to 50 results.
 *
 * Stale-response guard: every fetch carries an incrementing request id;
 * only the latest request's response is applied to state. This prevents
 * the "results flash and disappear" bug from earlier client-side search.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { useCompany } from '../../../context/CompanyContext';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 250;
const MIN_QUERY_LEN = 2;

async function authHeader() {
  const token = await getAuth().currentUser?.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

export const useClients = () => {
  const { companyId } = useCompany();
  const [clients, setClients]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]         = useState(null);
  const [hasMore, setHasMore]     = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Race-condition guard: each fetch bumps this counter; a late response
  // whose token !== current one is dropped.
  const reqIdRef = useRef(0);

  // Initial browse-mode load.
  const loadFirstPage = useCallback(async () => {
    if (!companyId) return;
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/clients/list`, {
        params: { limit: PAGE_SIZE },
        headers: await authHeader(),
      });
      if (reqIdRef.current !== myReq) return;      // stale
      setClients(res.data.items || []);
      setHasMore(!!res.data.hasMore);
      setNextCursor(res.data.nextCursor || null);
    } catch (err) {
      if (reqIdRef.current !== myReq) return;
      console.error('[useClients] loadFirstPage error:', err);
      setError(err);
    } finally {
      if (reqIdRef.current === myReq) setLoading(false);
    }
  }, [companyId]);

  const loadMore = useCallback(async () => {
    if (!companyId || !nextCursor || loadingMore || isSearching) return;
    const myReq = ++reqIdRef.current;
    setLoadingMore(true);
    try {
      const res = await axios.get(`${API}/api/clients/list`, {
        params: { limit: PAGE_SIZE, cursor: nextCursor },
        headers: await authHeader(),
      });
      if (reqIdRef.current !== myReq) return;
      setClients(prev => [...prev, ...(res.data.items || [])]);
      setHasMore(!!res.data.hasMore);
      setNextCursor(res.data.nextCursor || null);
    } catch (err) {
      if (reqIdRef.current !== myReq) return;
      console.error('[useClients] loadMore error:', err);
    } finally {
      if (reqIdRef.current === myReq) setLoadingMore(false);
    }
  }, [companyId, nextCursor, loadingMore, isSearching]);

  // Debounced search — server-side, full-dataset.
  useEffect(() => {
    if (!companyId) return;
    const q = (searchTerm || '').trim();
    if (q.length < MIN_QUERY_LEN) {
      // Clearing search → return to browse-mode first page.
      if (isSearching) {
        setIsSearching(false);
        loadFirstPage();
      }
      return;
    }
    setIsSearching(true);
    const handle = setTimeout(async () => {
      const myReq = ++reqIdRef.current;
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/clients/search`, {
          params: { q, limit: 50 },
          headers: await authHeader(),
        });
        if (reqIdRef.current !== myReq) return;    // stale
        setClients(res.data.items || []);
        setHasMore(false);
        setNextCursor(null);
      } catch (err) {
        if (reqIdRef.current !== myReq) return;
        console.error('[useClients] search error:', err);
        setError(err);
      } finally {
        if (reqIdRef.current === myReq) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchTerm, companyId, loadFirstPage, isSearching]);

  // Initial load.
  useEffect(() => { loadFirstPage(); }, [loadFirstPage]);

  return {
    clients,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    searchTerm,
    setSearchTerm,
    isSearching,
    refresh: loadFirstPage,
  };
};
